import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useResource, formatDate, daysUntil } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";
import type { Employee, Shift } from "../lib/types";

/**
 * Org chart, prototype-parity: rich cards (name, role, team, direct-report
 * count with collapse), search, zoom — and clicking a card opens a profile
 * drawer with contact, employment, this-week rostered hours, credential list
 * and notes. Built live from employee.direct_manager_id; no library.
 */

type Emp = Employee & {
    direct_manager_id?: string | null;
    team_id?: string | null;
    primary_job_role_id?: string | null;
    responsibility_id?: string | null;
    region?: string | null;
    notes?: string | null;
    start_date?: string;
};
interface NamedRow { id: string; name: string; }
interface CompDoc {
    employee_id: string; type_name: string | null; expiry_date: string | null;
    status: "none" | "current" | "expiring" | "expired";
}
interface ComplianceData { documents: CompDoc[]; }

interface TreeNode { employee: Emp; reports: TreeNode[]; }

const buildTree = (employees: Emp[]): TreeNode[] => {
    const byManager = new Map<string | null, Emp[]>();
    for (const e of employees) {
        const key = e.direct_manager_id ?? null;
        byManager.set(key, [...(byManager.get(key) ?? []), e]);
    }
    const grow = (e: Emp): TreeNode => ({
        employee: e,
        reports: (byManager.get(e.id) ?? []).map(grow),
    });
    const ids = new Set(employees.map((e) => e.id));
    return employees
        .filter((e) => !e.direct_manager_id || !ids.has(e.direct_manager_id))
        .map(grow);
};

const countReports = (n: TreeNode): number =>
    n.reports.length + n.reports.reduce((a, r) => a + countReports(r), 0);

const local = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const weekRange = () => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: `${local(monday)}T00:00:00Z`, to: `${local(sunday)}T23:59:59Z` };
};

export const OrgChart = () => {
    const employees = useResource<Emp[]>("/employees");
    const roles = useResource<NamedRow[]>("/roles");
    const teams = useResource<NamedRow[]>("/teams");
    const responsibilities = useResource<NamedRow[]>("/responsibilities");
    const compliance = useResource<ComplianceData>("/compliance/documents");
    const { from, to } = weekRange();
    const shifts = useResource<Shift[]>(`/shifts?from=${from}&to=${to}`);

    const [zoom, setZoom] = useState(1);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<Emp | null>(null);
    const [drawerTab, setDrawerTab] = useState<"profile" | "team" | "credentials">("profile");
    const open = (e: Emp) => { setSelected(e); setDrawerTab("profile"); };

    const roleName = useMemo(() => new Map((roles.data ?? []).map((r) => [r.id, r.name])), [roles.data]);
    const teamName = useMemo(() => new Map((teams.data ?? []).map((t) => [t.id, t.name])), [teams.data]);
    const positionName = useMemo(() => new Map((responsibilities.data ?? []).map((r) => [r.id, r.name])), [responsibilities.data]);
    // The organisational POSITION (Director, Coordinator, Team Leader…) leads
    // the card; the clinical role (RN/EN/SW) follows. Set positions on the
    // employee's Responsibility field; reporting lines via Direct manager.
    const positionOf = (e: Emp) => (e.responsibility_id ? positionName.get(e.responsibility_id) ?? null : null);

    const active = useMemo(
        () => (employees.data ?? []).filter((e) => e.employment_status === "active"),
        [employees.data]);
    const roots = useMemo(() => buildTree(active), [active]);
    const total = active.length;

    const matches = (e: Emp) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const role = e.primary_job_role_id ? roleName.get(e.primary_job_role_id) ?? "" : "";
        return e.full_name.toLowerCase().includes(q) || role.toLowerCase().includes(q);
    };

    const toggle = (id: string) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });

    const weekHours = (id: string) =>
        (shifts.data ?? [])
            .filter((s) => s.employee_id === id && s.status !== "cancelled")
            .reduce((a, s) => a + Math.max(0, new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 3_600_000, 0);

    const credsFor = (id: string) =>
        (compliance.data?.documents ?? []).filter((d) => d.employee_id === id);

    const Node = ({ node }: { node: TreeNode }) => {
        const e = node.employee;
        const isCollapsed = collapsed.has(e.id);
        const dim = !matches(e);
        const reportCount = countReports(node);
        return (
            <li>
                <div className={`org-card ${dim ? "dim" : ""}`} onClick={() => open(e)}>
                    <InitialsAvatar name={e.full_name} size={40} />
                    <div className="org-card-name">{e.full_name}</div>
                    <div className="org-card-role">
                        {positionOf(e) ?? (e.primary_job_role_id ? roleName.get(e.primary_job_role_id) ?? "—" : "—")}
                        {positionOf(e) && e.primary_job_role_id && ` · ${roleName.get(e.primary_job_role_id) ?? ""}`}
                    </div>
                    <div className="org-card-team">
                        {[e.team_id ? teamName.get(e.team_id) : null, e.region].filter(Boolean).join(" · ")}
                    </div>
                    <div className="org-card-foot" onClick={(ev) => ev.stopPropagation()}>
                        {node.reports.length > 0 ? (
                            <button className="org-mini-btn" onClick={() => toggle(e.id)}>
                                👥 {reportCount} {isCollapsed ? "▸ More" : "▾ Less"}
                            </button>
                        ) : <span />}
                        <button className="org-mini-btn" onClick={() => open(e)}>View profile ↗</button>
                    </div>
                </div>
                {node.reports.length > 0 && !isCollapsed && (
                    <ul>
                        {node.reports.map((r) => <Node key={r.employee.id} node={r} />)}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <div>
            <div className="toolbar" style={{ marginBottom: "0.6rem", padding: 0 }}>
                <input type="search" placeholder="Search name or role…" value={search}
                    onChange={(e) => setSearch(e.target.value)} />
                <button className="btn ghost" onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}>−</button>
                <button className="btn ghost" onClick={() => setZoom((z) => Math.min(1.4, z + 0.1))}>＋</button>
                <button className="btn ghost" onClick={() => { setZoom(1); setCollapsed(new Set()); }}>⤢</button>
                <div className="spacer" />
                <span className="muted">{total} people · zoom {Math.round(zoom * 100)}% · ▾ collapses a branch</span>
            </div>

            {roots.length === 0 ? (
                <div className="empty">No active employees to chart.</div>
            ) : (
                <div className="org-scroll card" style={{ padding: "1rem" }}>
                    <div className="org-tree" style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                        <ul>
                            {roots.map((r) => <Node key={r.employee.id} node={r} />)}
                        </ul>
                    </div>
                </div>
            )}

            {selected && (
                <>
                    <div className="org-drawer-mask" onClick={() => setSelected(null)} />
                    <aside className="org-drawer">
                        <div className="org-drawer-head">
                            <button className="org-drawer-close" onClick={() => setSelected(null)}>✕</button>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                                <InitialsAvatar name={selected.full_name} size={52} />
                                <div>
                                    <h2>{selected.full_name}</h2>
                                    <p>{positionOf(selected) ?? (selected.primary_job_role_id ? roleName.get(selected.primary_job_role_id) ?? "—" : "—")}
                                        {selected.team_id && teamName.get(selected.team_id) ? ` · ${teamName.get(selected.team_id)}` : ""}
                                        {selected.region ? ` · ${selected.region}` : ""}</p>
                                </div>
                            </div>
                            <div className="org-drawer-actions">
                                <Link className="btn ghost small" to={`/employees?id=${selected.id}`}>Open full record ↗</Link>
                            </div>
                            <div className="org-drawer-tabs">
                                <button className={drawerTab === "profile" ? "active" : ""} onClick={() => setDrawerTab("profile")}>Profile</button>
                                <button className={drawerTab === "team" ? "active" : ""} onClick={() => setDrawerTab("team")}>
                                    Team ({active.filter((x) => x.direct_manager_id === selected.id).length})
                                </button>
                                <button className={drawerTab === "credentials" ? "active" : ""} onClick={() => setDrawerTab("credentials")}>Credentials</button>
                            </div>
                        </div>
                        <div className="org-drawer-body">
                            {drawerTab === "profile" && (
                                <>
                                    <h3>Contact</h3>
                                    <div className="kv-line"><b>Mobile</b><span>{selected.contact_phone ?? "—"}</span></div>
                                    <div className="kv-line"><b>Email</b><span>{selected.email ?? "—"}</span></div>

                                    <h3>Employment</h3>
                                    {positionOf(selected) && <div className="kv-line"><b>Position</b><span>{positionOf(selected)}</span></div>}
                                    {selected.primary_job_role_id && <div className="kv-line"><b>Role</b><span>{roleName.get(selected.primary_job_role_id) ?? "—"}</span></div>}
                                    {selected.region && <div className="kv-line"><b>Region</b><span>{selected.region}</span></div>}
                                    <div className="kv-line"><b>Status</b><span className="chip pass">{selected.employment_status}</span></div>
                                    <div className="kv-line"><b>Type</b><span>{selected.employment_type.replace("_", " ")}</span></div>
                                    {selected.start_date && <div className="kv-line"><b>Started</b><span>{formatDate(selected.start_date)}</span></div>}
                                    <div className="kv-line"><b>This week</b><span>{weekHours(selected.id).toFixed(1)} hrs rostered</span></div>

                                    {selected.notes && (
                                        <>
                                            <h3>Notes</h3>
                                            <p className="muted" style={{ fontSize: "0.85rem" }}>{selected.notes}</p>
                                        </>
                                    )}
                                </>
                            )}

                            {drawerTab === "team" && (
                                <>
                                    <h3>Direct reports</h3>
                                    {active.filter((x) => x.direct_manager_id === selected.id).map((r) => (
                                        <div key={r.id} className="person-row clickable" onClick={() => open(r)}>
                                            <InitialsAvatar name={r.full_name} size={28} />
                                            <div className="person-main">
                                                <div className="name">{r.full_name}</div>
                                                <div className="sub">{positionOf(r) ?? (r.primary_job_role_id ? roleName.get(r.primary_job_role_id) ?? "" : "")}</div>
                                            </div>
                                            <span className={`chip ${r.employment_status === "active" ? "pass" : "mute"}`}>
                                                {r.employment_status.replace("_", " ")}
                                            </span>
                                        </div>
                                    ))}
                                    {active.filter((x) => x.direct_manager_id === selected.id).length === 0 && (
                                        <p className="empty">No direct reports.</p>
                                    )}
                                </>
                            )}

                            {drawerTab === "credentials" && (
                                <>
                                    <h3>Compliance &amp; credentials</h3>
                                    {credsFor(selected.id).map((d, i) => (
                                        <div className="kv-line" key={i}>
                                            <b>
                                                <span className={`chip ${d.status === "expired" ? "block" : d.status === "expiring" ? "warn" : "pass"}`}
                                                    style={{ marginRight: 6 }}>●</span>
                                                {d.type_name ?? "Document"}
                                            </b>
                                            <span>{d.expiry_date
                                                ? `exp ${formatDate(d.expiry_date)}${daysUntil(d.expiry_date) < 0 ? " — EXPIRED" : ""}`
                                                : "no expiry"}</span>
                                        </div>
                                    ))}
                                    {credsFor(selected.id).length === 0 && <p className="empty">No documents recorded.</p>}
                                </>
                            )}
                        </div>
                    </aside>
                </>
            )}
        </div>
    );
};
